import { IBillingAddress, IBillingCard } from "@/types/billing.types";
import type { ChangeEvent } from "react";

export type PaymentFormCompanyProps = {
  onHaveNotVatID: () => void;
  handleChangeForm: (key: keyof IPaymentForm, val: string) => void;
  handleChangeVat: (ev: ChangeEvent<HTMLInputElement>) => void;
  resetForm: () => void;
  billingAddressErrors: BillingAddressErrorType;
  isThereBillingErrors: boolean;
  setIsThereBillingErrors: (val: boolean) => void;
  isScrollEnabled: boolean;
  vatError: string;
  form: IPaymentForm;
  isUS: boolean;
  isCA: boolean;
  isEuCountry: boolean;
  forceCompany: boolean;
  refetchUserPlan: (state?: string | null) => void;
  transformedCountries: CheckoutCountryType[];
};

export type CheckoutCountryType = {
  label: string;
  value: string;
};

export enum BillingTypeEnum {
  COMPANY = "Company",
  PRIVATE = "Private",
}

export interface IPaymentForm {
  company: string;
  vat: string;
  name: string;
  line1: string;
  postal_code: string;
  email: string;
  country: string;
  city: string;
  state: string;
}

export type CheckoutStateType = {
  form: IPaymentForm;
  billingErrors: BillingAddressErrorType;
  isThereBillingErrors: boolean;
  billingType: BillingTypeEnum | null;
  billingInfo: BillingInfoType;
  isHaveNotVat: boolean;
  paymentMethodID: unknown;
  confirmLoading: boolean;
  vatError: string;
  postalCodeError: string;
  mainError: string;
  fakeScroll: boolean;
  isPostalCodeFocused: boolean;
  mutatePlanInfoLoading: boolean;
  totalPrice: string;
  setTotalPrice: (totalPrice: string) => void;
  setPostalCodeError: (postalCodeError: string) => void;
  setMutatePlanInfoLoading: (loading: boolean) => void;
  setFakeScroll: (fakeScroll: boolean) => void;
  setPostalCodeFocused: (isPostalCodeFocused: boolean) => void;
  clearForCountryChange: () => void;
  setHaveNotVat: (isHaveNotVat: boolean) => void;
  setVatError: (vatError: string) => void;
  setIsThereBillingErrors: (isThereBillingErrors: boolean) => void;
  setMainError: (mainError: string) => void;
  setPaymentMethodID: (paymentMethodID: unknown) => void;
  setConfirmLoading: (confirmLoading: boolean) => void;
  setBillingInfo: (billingInfo: BillingInfoType) => void;
  setBillingType: (billingType: BillingTypeEnum) => void;
  setBillingErrors: (errors: BillingAddressErrorType) => void;
  onChangeBillingErrors: (key: keyof BillingAddressErrorType, value: string) => void;
  setForm: (form: IPaymentForm) => void;
  onChange: (key: keyof IPaymentForm, value: string) => void;
  clearErrors: () => void;
  clear: () => void;
};

export type BillingInfoType = {
  total: string;
  price: string;
  plan_name: string;
  subTotal: string;
  periodIndicatorMessage?: string;
  lines: {
    total: string;
    description: string;
  }[];
  discount: string;
  discounts?: {
    name: string;
    percent_off: number | null;
    amount_off: number | null;
    currency: string | null;
  }[];
  tax: string;
  taxes?: {
    name: "VAT" | "TAX";
    percentage: number;
  }[];
  metadata: {
    full_name: string;
    is_company: boolean;
    vat: string;
    force_is_company: boolean;
  };
};

export interface IUserBillingInfo extends BillingInfoType {
  billingDetails: {
    address: {
      country: string;
      city: string;
      line1: string;
      postal_code: string;
      state: string;
    };
    name: string;
    email: string;
  };
}

export interface IBillingTax {
  country: string;
  tax_id: string;
}

export type ValidatePostalCodeType = { postal_code: string } & Partial<{
  is_company: boolean;
  country: string;
  state: string;
  city: string;
  line1: string;
}>;

export interface ISavedCardInfo {
  billing_details: IBillingAddress;
  card: IBillingCard;
  id: string;
}

export type BillingAddressErrorType = Omit<IPaymentForm, "country">;
